import type { ReactNode } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import LoginPage from '@/features/auth/LoginPage'
import MagicLinkPage from '@/features/auth/MagicLinkPage'
import MagicVerifyPage from '@/features/auth/MagicVerifyPage'
import RegisterPage from '@/features/auth/RegisterPage'
import { useBootstrapSession } from '@/features/auth/useBootstrapSession'
import SettingsPage from '@/features/account/SettingsPage'
import CartPage from '@/features/cart/CartPage'
import CheckoutPage from '@/features/cart/CheckoutPage'
import ShoppingListPage from '@/features/list/ShoppingListPage'
import OrderDetailPage from '@/features/orders/OrderDetailPage'
import OrdersPage from '@/features/orders/OrdersPage'
import PickingListPage from '@/features/portal/PickingListPage'
import SupplierCatalogPage from '@/features/portal/SupplierCatalogPage'
import SupplierCustomersPage from '@/features/portal/SupplierCustomersPage'
import ProductsPage from '@/features/products/ProductsPage'
import PublicInvitePage from '@/features/public/PublicInvitePage'
import LegalPage from '@/features/public/LegalPage'
import PublicOrderPage from '@/features/public/PublicOrderPage'
import UnsubscribePage from '@/features/public/UnsubscribePage'
import SupplierDetailPage from '@/features/suppliers/SupplierDetailPage'
import SuppliersPage from '@/features/suppliers/SuppliersPage'
import TemplatesPage from '@/features/templates/TemplatesPage'
import DashboardPage from '@/pages/DashboardPage'
import HealthPage from '@/pages/HealthPage'
import { useAuthStore } from '@/stores/authStore'

function RequireAuth({ children }: { children: ReactNode }) {
  const session = useAuthStore((s) => s.session)
  return session ? <>{children}</> : <Navigate to="/login" replace />
}

function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const session = useAuthStore((s) => s.session)
  return session ? <Navigate to="/" replace /> : <>{children}</>
}

function Home() {
  const session = useAuthStore((s) => s.session)

  if (!session) return <Navigate to="/login" replace />
  // Και οι δύο πλευρές έχουν πλέον αρχική: το `/dashboard` επιστρέφει
  // διαφορετικό σχήμα ανά τύπο οργανισμού.
  return <DashboardPage />
}

export default function App() {
  const { t } = useTranslation()
  useBootstrapSession()
  const isBootstrapping = useAuthStore((s) => s.isBootstrapping)

  // Μέχρι να απαντήσει το /auth/refresh δεν είναι γνωστό αν υπάρχει συνεδρία.
  // Χωρίς αυτή την αναμονή, μια ανανέωση σελίδας θα πετούσε τον συνδεδεμένο
  // χρήστη στην οθόνη σύνδεσης για ένα κλάσμα δευτερολέπτου.
  if (isBootstrapping) {
    return (
      <div className="flex min-h-full items-center justify-center text-gray-500">
        {t('common.loading')}
      </div>
    )
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <RedirectIfAuthenticated>
            <LoginPage />
          </RedirectIfAuthenticated>
        }
      />
      <Route
        path="/register"
        element={
          <RedirectIfAuthenticated>
            <RegisterPage />
          </RedirectIfAuthenticated>
        }
      />
      <Route path="/magic-link" element={<MagicLinkPage />} />
      <Route path="/auth/magic" element={<MagicVerifyPage />} />
      <Route path="/status" element={<HealthPage />} />

      {/* Δημόσιες σελίδες: ο προμηθευτής δεν έχει λογαριασμό (§11) */}
      <Route path="/p/order/:token" element={<PublicOrderPage />} />
      <Route path="/p/invite/:token" element={<PublicInvitePage />} />
      <Route path="/p/unsubscribe/:token" element={<UnsubscribePage />} />
      <Route path="/terms" element={<LegalPage />} />
      <Route path="/privacy" element={<LegalPage />} />

      <Route path="/" element={<Home />} />
      <Route
        path="/suppliers"
        element={
          <RequireAuth>
            <SuppliersPage />
          </RequireAuth>
        }
      />
      <Route
        path="/suppliers/:supplierId"
        element={
          <RequireAuth>
            <SupplierDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path="/list"
        element={
          <RequireAuth>
            <ShoppingListPage />
          </RequireAuth>
        }
      />
      <Route
        path="/cart"
        element={
          <RequireAuth>
            <CartPage />
          </RequireAuth>
        }
      />
      <Route
        path="/templates"
        element={
          <RequireAuth>
            <TemplatesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/settings"
        element={
          <RequireAuth>
            <SettingsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/products"
        element={
          <RequireAuth>
            <ProductsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/checkout"
        element={
          <RequireAuth>
            <CheckoutPage />
          </RequireAuth>
        }
      />
      <Route
        path="/orders"
        element={
          <RequireAuth>
            <OrdersPage />
          </RequireAuth>
        }
      />
      <Route
        path="/orders/:orderId"
        element={
          <RequireAuth>
            <OrderDetailPage />
          </RequireAuth>
        }
      />

      {/* Portal προμηθευτή (§11, πλευρά προμηθευτή) */}
      <Route
        path="/supplier/customers"
        element={
          <RequireAuth>
            <SupplierCustomersPage />
          </RequireAuth>
        }
      />
      <Route
        path="/supplier/catalog"
        element={
          <RequireAuth>
            <SupplierCatalogPage />
          </RequireAuth>
        }
      />
      <Route
        path="/supplier/picking-list"
        element={
          <RequireAuth>
            <PickingListPage />
          </RequireAuth>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
